/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Stack, Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '.'
import {useProducts, useProduct} from '@salesforce/commerce-sdk-react'
import {getDisplayVariationValues} from '@app/utils/product-utils'

const getOrderedAttributeValues = (variationValues, variationAttributes) => {
    if (!variationValues) return []

    const entries = Object.entries(variationValues)
    
    // Map attribute IDs to their display names (for multi-language support)
    const idToDisplayName = {}
    if (variationAttributes && Array.isArray(variationAttributes)) {
        variationAttributes.forEach(attr => {
            if (attr?.id && attr?.name) {
                idToDisplayName[attr.id.toLowerCase()] = attr.name
            }
        })
    }
    
    const pick = (attributeIds) => {
        // First try to match by attribute ID -> display name mapping
        for (const id of attributeIds) {
            const displayName = idToDisplayName[id.toLowerCase()]
            if (displayName && variationValues[displayName]) {
                return variationValues[displayName]
            }
        }
        
        // Fallback to matching by display name (for backwards compatibility)
        for (const id of attributeIds) {
            const normalized = id.toLowerCase()
            const match = entries.find(([key]) => {
                const keyNormalized = key.toLowerCase()
                return keyNormalized === normalized || keyNormalized.includes(normalized)
            })
            if (match) return match[1]
        }
        
        return null
    }

    const color = pick(['color', 'colour'])
    const size = pick(['size'])
    const length = pick(['length'])

    return [color, size, length].filter(Boolean)
}

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, fontSize = 'sm', ...props}) => {
    const variant = useItemVariant()
    const intl = useIntl()
    const isGiftCard = variant?.c_isGiftCard || variant?.c_isEGiftCard

    const findDisplayName = (attrId, rawValue) => {
        if (!rawValue) return null
        const attr = variant?.variationAttributes?.find((a) => a.id === attrId)
        return attr?.values?.find((v) => v.value === rawValue)?.name || rawValue
    }

    // Check if all variationAttributes have values arrays before calling getDisplayVariationValues
    const hasValidVariationAttributes = variant?.variationAttributes?.every(attr =>
        attr.values && Array.isArray(attr.values)
    )

    const variationValues = hasValidVariationAttributes && variant?.variationValues
        ? getDisplayVariationValues(
            variant.variationAttributes,
            variant.variationValues
        )
        : null

    const orderedAttributeValues = getOrderedAttributeValues(variationValues, variant?.variationAttributes)
    const orderedAttributesText = orderedAttributeValues.join(', ')

    const giftCardCardTypeText = (() => {
        if (!isGiftCard || !variant?.variationValues) return null
        return findDisplayName('cardType', variant.variationValues.cardType)
    })()
    const giftCardColorText = orderedAttributeValues[0] || null
    const giftCardAdditionalAttributesText = orderedAttributeValues.slice(1).join(', ')

    // get variant info for bundled products in cart page and order history page
    const productBundleIds =
        variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: productBundleVariantData, isLoading: bundleVariantIsLoading} = useProducts(
        {
            parameters: {
                ids: productBundleIds,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && productBundleIds),
            select: (result) => {
                // formats response so we can easily display child quantity/variant selection
                return result?.data?.map((item) => {
                    const quantity = variant?.bundledProductItems.find(
                        (childProduct) => childProduct.productId === item.id
                    )?.quantity
                    return {
                        ...item,
                        quantity,
                        variationValues: item?.variationAttributes?.every(attr => attr.values && Array.isArray(attr.values)) && item?.variationValues
                            ? getDisplayVariationValues(
                                item.variationAttributes,
                                item.variationValues
                            )
                            : null
                    }
                })
            }
        }
    )

    // get bundle product data for wishlist page
    const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
        {
            parameters: {
                id: variant?.id,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && !productBundleIds)
        }
    )

    return (
        <Stack spacing={0} minWidth={0} {...props}>
            {!isGiftCard && orderedAttributesText && (
                <Text
                    textStyle="bodyXsSRegular"
                    noOfLines={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxWidth="100%"
                    data-testid="product-attributes"
                >
                    {orderedAttributesText}
                </Text>
            )}

            {isGiftCard &&
                (giftCardCardTypeText || giftCardColorText || giftCardAdditionalAttributesText) && (
                    <Text textStyle={'bodyXsSRegular'}>
                        {giftCardCardTypeText}
                        {giftCardColorText && (
                            <>
                                {giftCardCardTypeText ? ', ' : ''}
                                <FormattedMessage
                                    defaultMessage="Style"
                                    id="item_attributes.label.style"
                                />{' '}
                                {giftCardColorText}
                            </>
                        )}
                        {giftCardAdditionalAttributesText &&
                            `${giftCardCardTypeText || giftCardColorText ? ', ' : ''}${giftCardAdditionalAttributesText}`}
                    </Text>
                )}

            {includeQuantity && (
                <Text
                    lineHeight="18px"
                    color="black"
                    fontSize={fontSize}
                    noOfLines={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    maxWidth="100%"
                >
                    <FormattedMessage
                        defaultMessage="Quantity: {quantity}"
                        values={{quantity: variant?.quantity}}
                        id="item_attributes.label.quantity"
                    />
                </Text>
            )}

            {!productBundleIsLoading && productBundleData && !productBundleVariantData && (
                <Box>
                    {productBundleData?.bundledProducts.map(({product, quantity}) => (
                        <Box marginTop={2} key={product.id}>
                            <Text fontSize={fontSize} color="gray.700" as="b">
                                {product?.name}
                            </Text>
                            <Text fontSize={fontSize} color="gray.700">
                                {intl.formatMessage({
                                    defaultMessage: 'Qty',
                                    id: 'add_to_cart_modal.label.quantity'
                                })}
                                : {quantity}
                            </Text>
                        </Box>
                    ))}
                </Box>
            )}

            {!bundleVariantIsLoading && productBundleVariantData && (
                <Box>
                    <Text fontSize={fontSize} marginTop={3} fontWeight={500}>
                        {intl.formatMessage({
                            defaultMessage: 'Selected Options',
                            id: 'item_attributes.label.selected_options'
                        })}
                        :
                    </Text>
                    {productBundleVariantData?.map(
                        ({variationValues, name: productName, quantity, id}) => {
                            return (
                                <Box key={id} marginTop={2}>
                                    <Text fontSize={fontSize} color="gray.700" as="b">
                                        {productName}
                                    </Text>
                                    <Text fontSize={fontSize} color="gray.700">
                                        {intl.formatMessage({
                                            defaultMessage: 'Qty',
                                            id: 'add_to_cart_modal.label.quantity'
                                        })}
                                        : {quantity}
                                    </Text>
                                    {variationValues && Object.keys(variationValues).map((key) => {
                                        const selectedVariant = `${key}: ${variationValues[key]}`
                                        return (
                                            <Text
                                                fontSize={fontSize}
                                                color="gray.700"
                                                key={selectedVariant}
                                            >
                                                {selectedVariant}
                                            </Text>
                                        )
                                    })}
                                </Box>
                            )
                        }
                    )}
                </Box>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
}

export default ItemAttributes
